import { push } from 'connected-react-router';
import { isArray, isEmpty } from 'lodash';
import { paths } from '../../../routes/paths';
import CoreAPIService from '../../../utils/core-api';
import { fetchAction } from '../../actions';
import { RiderById, RiderFavorites, RiderReserver } from '../api-types';
import UserUtils from '../../../utils/userUtils';
import envConfig from '../../../config';

export const name: string = 'riders';

export const types = {
    FIND_RIDER: name + '/FIND_RIDER',
    FIND_RIDERS_WITH_PHONE: name + '/FIND_RIDERS_WITH_PHONE',
    SET_FOUND_RIDER: name + '/SET_FOUND_RIDER',
    RESET_FOUND_RIDERS: name + '/RESET_FOUND_RIDERS',
    EDIT_RIDER: name + '/EDIT_RIDER',
    EDIT_USER: name + '/EDIT_USER',
    GET_RIDER: name + '/GET_RIDER',
    GET_USER: name + '/GET_USER', // New version of GET_RIDER
    ANON_RIDER: name + '/ANON_RIDER',
    PATCH_RIDER: name + '/PATCH_RIDER',
    RESET_RIDER_PASSWORD: name + '/RESET_RIDER_PASSWORD',
    GET_RIDER_FAVORITE: name + '/GET_RIDER_FAVORITE',
    CHANGE_RIDER_COMPANY: name + '/CHANGE_RIDER_COMPANY',
    CHANGE_USER_GROUP: name + '/CHANGE_USER_GROUP',
    SET_ADMIN_COMPANY: name + '/SET_ADMIN_COMPANY',
    SAVE_RIDER_PROMO: name + '/SAVE_RIDER_PROMO',
    IMPERSONATE_USER: name + '/IMPERSONATE_USER',
};

interface RidersState {
    users: RiderById;
    riders: RiderById;
    favorites: RiderFavorites;
    foundRider: RiderReserver;
    foundRiders: RiderReserver[];
}

const initialState: RidersState = {
    users: {} as RiderById,
    riders: {} as RiderById,
    favorites: {} as RiderFavorites,
    foundRider: {} as RiderReserver,
    foundRiders: [] as RiderReserver[],
};

const reducer = (state = initialState, action): RidersState => {
    const payload = action.payload;
    const parameters = action.parameters;
    const id = parameters ? parameters.id : 0;
    switch (action.type) {
        case types.FIND_RIDER:
        case types.SET_FOUND_RIDER:
            return {
                ...state,
                foundRider: payload,
            };
        case types.FIND_RIDERS_WITH_PHONE:
            if (isEmpty(payload)) {
                return {
                    ...state,
                    foundRiders: [],
                };
            }
            return {
                ...state,
                foundRiders: isArray(payload) ? payload : [payload],
            };
        case types.RESET_FOUND_RIDERS:
            return {
                ...state,
                foundRiders: [],
            };
        case types.GET_RIDER:
            return {
                ...state,
                riders: { ...state.riders, [id]: payload },
            };
        case types.GET_USER:
            return {
                ...state,
                users: { ...state.users, [id]: payload },
            };
        case types.EDIT_USER:
            return state;
        case types.EDIT_RIDER:
        case types.ANON_RIDER:
        case types.PATCH_RIDER:
            return {
                ...state,
                riders: {
                    ...state.riders,
                    [id]: {
                        ...state.riders[id],
                        ...payload,
                    },
                },
            };
        case types.GET_RIDER_FAVORITE:
            return {
                ...state,
                favorites: payload,
            };
        case types.SET_ADMIN_COMPANY:
            return {
                ...state,
                riders: {
                    ...state.riders,
                    [id]: {
                        ...state.riders[id],
                        active_company_membership: payload,
                    },
                },
            };
        case types.CHANGE_RIDER_COMPANY:
            return {
                ...state,
                riders: {
                    ...state.riders,
                    [id]: {
                        ...state.riders[id],
                        active_company_membership: payload.active_company_membership,
                        company_memberships: payload.company_memberships,
                    },
                },
            };
        case types.CHANGE_USER_GROUP:
            return state;
        case types.SAVE_RIDER_PROMO:
            return state;
        default:
            return state;
    }
};
export default reducer;

export const actions = {
    findRider: (payload) => fetchAction(CoreAPIService.post(`/core/riders/find`, payload), types.FIND_RIDER, payload),
    findRidersWithPhone: (payload) =>
        fetchAction(CoreAPIService.post(`/core/riders/findWithPhone`, payload), types.FIND_RIDERS_WITH_PHONE, payload),
    setFoundRider: (rider: RiderReserver) => ({
        type: types.SET_FOUND_RIDER,
        payload: rider,
    }),
    resetFoundRiders: (riders: RiderReserver[]) => ({
        type: types.RESET_FOUND_RIDERS,
        payload: {},
    }),
    getRider: (id) => fetchAction(CoreAPIService.get(`/core/riders/${id}`), types.GET_RIDER, { id }),
    getUser: (id) =>
        fetchAction(CoreAPIService.get(`internal/user/${id}`, null, null, envConfig.privapi_url), types.GET_USER, {
            id,
        }),
    editRider: (id, payload, onDone = null) =>
        fetchAction(
            CoreAPIService.put(`/core/riders/${id}`, payload),
            types.EDIT_RIDER,
            { id },
            {
                success: (dispatch) => {
                    onDone?.();
                    dispatch(push(`${paths.riders}/${id}`));
                },
            },
        ),
    editUser: (id, payload, onDone = null) =>
        fetchAction(
            CoreAPIService.put(`/internal/user/${id}`, payload, null, envConfig.id_url),
            types.EDIT_USER,
            { id },
            {
                success: () => onDone?.(),
            },
        ),
    resetRiderPassword: (email, callback) =>
        fetchAction(
            CoreAPIService.post(`/core/password_reset`, { email }),
            types.RESET_RIDER_PASSWORD,
            {},
            { success: callback },
        ),
    saveRiderPromo: (id, payload) =>
        fetchAction(
            CoreAPIService.post(`/core/promotions`, payload),
            types.SAVE_RIDER_PROMO,
            { id },
            {
                success: (dispatch) => {
                    dispatch(push(`${paths.riders}/${id}`));
                },
            },
        ),
    patchRider: (id, payload, onDone) =>
        fetchAction(
            CoreAPIService.patch(`/core/riders/${id}`, payload),
            types.PATCH_RIDER,
            { id },
            { success: onDone },
        ),
    anonRider: (id, ok) => {
        const params = ok ? '' : '?cancel=1';
        return fetchAction(CoreAPIService.delete(`/core/riders/${id}${params}`), types.ANON_RIDER, { id });
    },
    getRiderFavorite: (id) =>
        fetchAction(CoreAPIService.get(`/core/riders/${id}/favorites`), types.GET_RIDER_FAVORITE, { id }),
    changeRiderCompany: (id, idc) =>
        fetchAction(
            CoreAPIService.put(`/core/riders/${id}/company`, { idc }),
            types.CHANGE_RIDER_COMPANY,
            { id },
            {
                success: (dispatch) => {
                    dispatch(push(paths.riders));
                },
            },
        ),
    changeUserGroup: (id, payload, onDone = null, onFailed = null) =>
        fetchAction(
            CoreAPIService.put(`/internal/user/change_group_in_company/${id}`, payload, null, envConfig.id_url),
            types.CHANGE_USER_GROUP,
            { id },
            {
                success: () => onDone?.(),
                // LT-876: a move that fails answers 409 and puts the revoked
                // memberships back, so the caller has to hear about it - a
                // sheet listing several of them fires one call per row
                error: () => onFailed?.(),
            },
        ),
    setAdminCompany: (id, idc, payload) =>
        fetchAction(CoreAPIService.put(`/core/companies/${idc}/memberships/${id}`, payload), types.SET_ADMIN_COMPANY, {
            id,
        }),
    impersonateUser: (id, callback) =>
        fetchAction(
            CoreAPIService.get(`/auth/tatayet/${id}`, {}, { credential: true }, envConfig.id_url),
            types.IMPERSONATE_USER,
            { id },
            {
                success: () => {
                    UserUtils.removeUser();
                    UserUtils.removeFreeSeatingInfos();
                    callback();
                },
            },
        ),
};

const localState = (state: any): RidersState => state[name] || initialState;
export const selectors = {
    users: (state) => localState(state).users,
    riders: (state) => localState(state).riders,
    favorites: (state) => localState(state).favorites,
    foundRider: (state) => localState(state).foundRider,
    foundRiders: (state) => localState(state).foundRiders,
};
